// PublicSignup.jsx — "Programs" tab inside My Portal.
//
// Lets the portal user enrol a participant in a Coaching Program: search
// everyone already in BOMS by part of their name and pick them (contact
// details pre-fill), or type a new name.
// Submits to POST /api/public/participants/:token which creates the
// Participant + ParticipantProgramTypes link and SMSes the program's
// signup URL to the contact mobile entered on the form.

import React, { useEffect, useRef, useState } from 'react';
import { publicFetch } from '../utils/publicApi';

const API_BASE = process.env.REACT_APP_API_BASE || 'https://referee-roster-api-v2-hqghffesdfegd2dq.australiaeast-01.azurewebsites.net/api';

const labelStyle = { display: 'block', fontSize: 13, fontWeight: 600, marginBottom: 4, color: '#444' };
const inputStyle = { padding: '10px 12px', border: '1px solid #ccc', borderRadius: 6, fontSize: 15, width: '100%', boxSizing: 'border-box' };
const btnPrimary = { padding: '10px 18px', background: '#A4161A', color: 'white', border: 'none', borderRadius: 6, fontWeight: 700, fontSize: 15, cursor: 'pointer' };
const btnDisabled = { ...btnPrimary, background: '#999', cursor: 'not-allowed' };

const AGE_GROUPS = [
    'Under 5', 'Under 6', 'Under 7', 'Under 8', 'Under 9', 'Under 10',
    'Under 11', 'Under 12', 'Under 13', 'Under 14', 'Under 15', 'Under 16',
    'Under 17', 'Under 18', 'Open / Senior'
];

export default function PublicSignup({ token, initialProgramTypeId, onSeedConsumed }) {
    const [programs, setPrograms] = useState([]);
    const [programsLoading, setProgramsLoading] = useState(true);
    const [programsError, setProgramsError] = useState('');

    const [selectedProgramId, setSelectedProgramId] = useState('');

    // If the parent (PublicPortal) passed an initialProgramTypeId via the
    // "Add Participant" button on the Coach tab, pre-select that program
    // once the program list has loaded. Then notify the parent so the
    // seed isn't re-applied on subsequent renders.
    useEffect(() => {
        if (!initialProgramTypeId) return;
        if (programs.length === 0) return;
        const match = programs.find(p => String(p.id) === String(initialProgramTypeId));
        if (match) {
            setSelectedProgramId(String(initialProgramTypeId));
            if (onSeedConsumed) onSeedConsumed();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [initialProgramTypeId, programs]);
    // Name search: the typed text, the matches, and the picked participant.
    const [nameQuery, setNameQuery] = useState('');
    const [matches, setMatches] = useState([]);
    const [showMatches, setShowMatches] = useState(false);
    const [picked, setPicked] = useState(null);
    const searchSeq = useRef(0);
    const [form, setForm] = useState({
        ageGroup: '',
        mobile: '',
        email: '',
        contactPersonName: '',
        notes: ''
    });
    const [submitting, setSubmitting] = useState(false);
    const [submitResult, setSubmitResult] = useState(null);

    useEffect(() => {
        let active = true;
        async function load() {
            setProgramsLoading(true);
            setProgramsError('');
            try {
                const res = await fetch(`${API_BASE}/public/program-types/${token}`);
                if (!res.ok) {
                    const d = await res.json().catch(() => ({}));
                    throw new Error(d.error || `HTTP ${res.status}`);
                }
                const data = await res.json();
                if (active) setPrograms(data || []);
            } catch (err) {
                if (active) setProgramsError(err.message);
            } finally {
                if (active) setProgramsLoading(false);
            }
        }
        load();
        return () => { active = false; };
    }, [token]);

    const selectedProgram = programs.find(p => String(p.id) === String(selectedProgramId)) || null;

    // Debounced search once 2+ characters are typed (and nobody is picked).
    useEffect(() => {
        if (picked) return undefined;
        const q = nameQuery.trim();
        if (q.length < 2) { setMatches([]); return undefined; }
        const seq = ++searchSeq.current;
        const timer = setTimeout(async () => {
            try {
                const res = await publicFetch(`${API_BASE}/public/participants-search/${token}?q=${encodeURIComponent(q)}`);
                const data = res.ok ? await res.json() : [];
                if (seq === searchSeq.current) setMatches(Array.isArray(data) ? data : []);
            } catch {
                if (seq === searchSeq.current) setMatches([]);
            }
        }, 250);
        return () => clearTimeout(timer);
    }, [nameQuery, picked, token]);

    function pickParticipant(p) {
        setPicked(p);
        setNameQuery(`${p.firstName} ${p.lastName}`);
        setShowMatches(false);
        // Pre-fill what we know; anything missing can still be typed.
        setForm(prev => ({
            ...prev,
            ageGroup: AGE_GROUPS.includes(p.ageGroup) ? p.ageGroup : prev.ageGroup,
            mobile: p.mobile || prev.mobile,
            email: p.email || prev.email,
            contactPersonName: p.contactPersonName || prev.contactPersonName,
        }));
    }

    function clearPicked() {
        setPicked(null);
        setNameQuery('');
        setMatches([]);
        setForm({ ageGroup: '', mobile: '', email: '', contactPersonName: '', notes: '' });
    }

    const typedName = nameQuery.trim().split(/\s+/).filter(Boolean);
    const nameOk = !!picked || typedName.length >= 2;

    function updateField(key, value) {
        setForm(prev => ({ ...prev, [key]: value }));
    }

    function resetForm() {
        setForm({ ageGroup: '', mobile: '', email: '', contactPersonName: '', notes: '' });
        setPicked(null);
        setNameQuery('');
        setMatches([]);
        setSelectedProgramId('');
        setSubmitResult(null);
    }

    async function handleSubmit(e) {
        e.preventDefault();
        if (!selectedProgramId || !nameOk || !form.mobile.trim()) return;
        setSubmitting(true);
        setSubmitResult(null);
        try {
            const res = await fetch(`${API_BASE}/public/participants/${token}`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    programTypeId: parseInt(selectedProgramId, 10),
                    ...(picked ? { participantId: picked.id } : { fullName: nameQuery.trim() }),
                    ageGroup: form.ageGroup,
                    mobile: form.mobile.trim(),
                    email: form.email.trim(),
                    contactPersonName: form.contactPersonName.trim(),
                    notes: form.notes.trim()
                })
            });
            const data = await res.json();
            if (!res.ok) {
                setSubmitResult({ ok: false, error: data.error || `HTTP ${res.status}` });
                return;
            }
            setSubmitResult({ ok: true, ...data });
        } catch (err) {
            setSubmitResult({ ok: false, error: err.message });
        } finally {
            setSubmitting(false);
        }
    }

    if (programsLoading) {
        return <div className="public-tab-loading">Loading programs…</div>;
    }

    if (programsError) {
        return <div className="public-tab-error">Couldn't load programs: {programsError}</div>;
    }

    if (programs.length === 0) {
        return (
            <div className="public-empty-state">
                <p>No coaching programs are available for signup right now.</p>
            </div>
        );
    }

    if (submitResult && submitResult.ok) {
        return (
            <div style={{ padding: '1rem' }}>
                <div style={{ background: '#e8f5e9', border: '1px solid #a5d6a7', borderRadius: 8, padding: 16 }}>
                    <h3 style={{ margin: '0 0 8px 0', color: '#2e7d32' }}>Allocated ✓</h3>
                    <p style={{ margin: '0 0 8px 0' }}>
                        <strong>{submitResult.firstName} {submitResult.lastName}</strong> was allocated to <strong>{submitResult.programTypeName}</strong>.
                    </p>
                    {submitResult.signupUrl ? (
                        <p style={{ margin: '0 0 8px 0', fontSize: 14 }}>
                            Sign up link:{' '}
                            <a href={submitResult.signupUrl} target="_blank" rel="noopener noreferrer">{submitResult.signupUrl}</a>
                        </p>
                    ) : null}
                    {submitResult.sms?.ok ? (
                        <p style={{ margin: '8px 0 0 0', fontSize: 13, color: '#1b5e20' }}>
                            ✓ The signup link has been SMS'd to {form.mobile}.
                        </p>
                    ) : (
                        <p style={{ margin: '8px 0 0 0', fontSize: 13, color: '#c62828' }}>
                            SMS not sent: {submitResult.sms?.error || 'unknown reason'}.
                            {submitResult.signupUrl ? ' You can copy the link above and share it manually.' : ''}
                        </p>
                    )}
                </div>
                <div style={{ marginTop: 16, display: 'flex', gap: 8 }}>
                    <button onClick={resetForm} style={btnPrimary}>Allocate another</button>
                </div>
            </div>
        );
    }

    return (
        <div style={{ padding: '0.5rem' }}>
            <p style={{ color: '#444', fontSize: 14, margin: '0 0 16px 0' }}>
                Allocate a participant to one of our coaching programs. The signup link will be sent
                via SMS to the contact mobile you provide.
            </p>

            <form onSubmit={handleSubmit} style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                <div>
                    <label style={labelStyle}>Coaching Program</label>
                    <select
                        value={selectedProgramId}
                        onChange={e => setSelectedProgramId(e.target.value)}
                        style={inputStyle}
                        required
                    >
                        <option value="">— select a program —</option>
                        {programs.map(p => (
                            <option key={p.id} value={p.id}>
                                {p.name}{p.code ? ` (${p.code})` : ''}{p.location ? ` · ${p.location}` : ''}
                            </option>
                        ))}
                    </select>
                    {selectedProgram && !selectedProgram.signupUrl && (
                        <small style={{ color: '#c62828', fontSize: 12, display: 'block', marginTop: 4 }}>
                            Heads up: this program doesn't have a signup URL set yet. The participant will still be
                            allocated, but no SMS will be sent.
                        </small>
                    )}
                </div>

                <div style={{ position: 'relative' }}>
                    <label style={labelStyle}>Participant</label>
                    <div style={{ display: 'flex', gap: 8 }}>
                        <input
                            type="text"
                            value={nameQuery}
                            onChange={e => { setNameQuery(e.target.value); setShowMatches(true); }}
                            onFocus={() => setShowMatches(true)}
                            onBlur={() => setTimeout(() => setShowMatches(false), 150)}
                            placeholder="Start typing their name…"
                            style={{ ...inputStyle, background: picked ? '#f1f8f2' : 'white' }}
                            readOnly={!!picked}
                            autoComplete="off"
                            required
                        />
                        {picked && (
                            <button type="button" onClick={clearPicked} style={{ ...btnPrimary, background: 'white', color: '#A4161A', border: '1px solid #A4161A', whiteSpace: 'nowrap' }}>
                                Change
                            </button>
                        )}
                    </div>
                    {showMatches && !picked && matches.length > 0 && (
                        <div style={{ position: 'absolute', zIndex: 10, left: 0, right: 0, top: '100%', marginTop: 4, background: 'white', border: '1px solid #ccc', borderRadius: 6, boxShadow: '0 6px 18px rgba(0,0,0,0.12)', maxHeight: 280, overflowY: 'auto' }}>
                            {matches.map(m => (
                                <button
                                    key={m.id}
                                    type="button"
                                    onMouseDown={e => e.preventDefault()}
                                    onClick={() => pickParticipant(m)}
                                    style={{ display: 'block', width: '100%', textAlign: 'left', padding: '10px 12px', border: 'none', borderBottom: '1px solid #f0f0f0', background: 'white', cursor: 'pointer', fontSize: 15 }}
                                >
                                    <strong>{m.firstName} {m.lastName}</strong>
                                    {m.ageGroup ? <span style={{ color: '#666' }}> · {m.ageGroup}</span> : null}
                                    {m.contactPersonName ? <span style={{ color: '#666', fontSize: 13 }}> · contact {m.contactPersonName}</span> : null}
                                </button>
                            ))}
                        </div>
                    )}
                    <small style={{ color: '#666', fontSize: 12, display: 'block', marginTop: 4 }}>
                        {picked
                            ? 'Already in SwishIQ. Their known contact details are filled in below.'
                            : nameQuery.trim().length >= 2 && matches.length === 0
                                ? 'No match. Type their first and last name to add them as a new participant.'
                                : 'Pick them from the list, or type a first and last name to add someone new.'}
                    </small>
                </div>

                <div>
                    <label style={labelStyle}>Age group</label>
                    <select value={form.ageGroup} onChange={e => updateField('ageGroup', e.target.value)} style={inputStyle}>
                        <option value="">— select —</option>
                        {AGE_GROUPS.map(ag => <option key={ag} value={ag}>{ag}</option>)}
                    </select>
                </div>

                <div>
                    <label style={labelStyle}>Contact person (parent / guardian)</label>
                    <input type="text" value={form.contactPersonName} onChange={e => updateField('contactPersonName', e.target.value)} style={inputStyle} />
                </div>

                <div>
                    <label style={labelStyle}>Contact mobile (Australian)</label>
                    <input type="tel" value={form.mobile} onChange={e => updateField('mobile', e.target.value)} placeholder="0412 345 678" style={inputStyle} required />
                    <small style={{ color: '#666', fontSize: 12 }}>The signup link is SMS'd to this number.</small>
                </div>

                <div>
                    <label style={labelStyle}>Contact email (optional)</label>
                    <input type="email" value={form.email} onChange={e => updateField('email', e.target.value)} style={inputStyle} />
                </div>

                <div>
                    <label style={labelStyle}>Notes (optional)</label>
                    <textarea value={form.notes} onChange={e => updateField('notes', e.target.value)} style={{ ...inputStyle, minHeight: 80, fontFamily: 'inherit' }} />
                </div>

                {submitResult && !submitResult.ok && (
                    <div style={{ padding: 10, background: '#ffebee', borderRadius: 6, fontSize: 13, color: '#c62828' }}>
                        {submitResult.error}
                    </div>
                )}

                <div>
                    <button
                        type="submit"
                        disabled={submitting || !selectedProgramId || !nameOk || !form.mobile}
                        style={(submitting || !selectedProgramId || !nameOk || !form.mobile) ? btnDisabled : btnPrimary}
                    >
                        {submitting ? 'Allocating…' : 'Allocate Participant'}
                    </button>
                </div>
            </form>
        </div>
    );
}
